import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { firebaseAuth, type User as FirebaseUser } from '@/lib/firebase';
import { authApi, type User, type BarMembership } from '@/api/endpoints/auth';
import { analytics } from '@/lib/analytics';
import toast from 'react-hot-toast';

interface AuthContextValue {
  firebaseUser: FirebaseUser | null;
  dbUser: User | null;
  managedBars: BarMembership[];
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  hasBarAccess: boolean;
  sendSignInLink: (email: string) => Promise<void>;
  completeSignInWithEmailLink: (url: string, email?: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [isFirebaseLoading, setIsFirebaseLoading] = useState(true);
  const queryClient = useQueryClient();

  // Listen to Firebase auth state
  useEffect(() => {
    const unsubscribe = firebaseAuth.onAuthChange((user) => {
      setFirebaseUser(user);
      setIsFirebaseLoading(false);
    });
    return unsubscribe;
  }, []);

  // Fetch backend user when Firebase user is available
  const { data: authData, isLoading: isDbLoading } = useQuery({
    queryKey: ['me', firebaseUser?.uid],
    queryFn: async () => {
      const response = await authApi.me();
      return response;
    },
    enabled: !!firebaseUser,
    retry: false,
  });

  const dbUser = authData?.user || null;
  const managedBars = authData?.managedBars || [];

  // Set analytics user properties when user data loads
  useEffect(() => {
    if (firebaseUser && dbUser) {
      const authMethod = firebaseUser.providerData[0]?.providerId || 'unknown';
      analytics.setUser(firebaseUser.uid, {
        auth_method: authMethod === 'google.com' ? 'google' : 'email',
        is_admin: dbUser.isAdmin ? 'true' : 'false',
        has_bar_access: (dbUser.isAdmin || managedBars.length > 0) ? 'true' : 'false',
      });
    }
  }, [firebaseUser, dbUser, managedBars.length]);

  const isLoading = isFirebaseLoading || (!!firebaseUser && isDbLoading);
  const isAuthenticated = !!firebaseUser && !!dbUser;
  const isAdmin = dbUser?.isAdmin === true;
  const hasBarAccess = isAdmin || managedBars.length > 0;

  const sendSignInLink = async (email: string) => {
    try {
      await firebaseAuth.sendSignInLink(email);
      toast.success('Sign-in link sent! Check your email.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send sign-in link';
      toast.error(message);
      throw error;
    }
  };

  const completeSignInWithEmailLink = async (url: string, email?: string) => {
    try {
      await firebaseAuth.completeSignInWithEmailLink(url, email);
      analytics.login('email');
      toast.success('Signed in successfully');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Sign in failed';
      toast.error(message);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      await firebaseAuth.signInWithGoogle();
      analytics.login('google');
      toast.success('Signed in successfully');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Sign in failed';
      toast.error(message);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseAuth.signOut();
      analytics.logout();
      analytics.setUser(null);
      queryClient.clear();
      toast.success('Signed out');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Sign out failed';
      toast.error(message);
      throw error;
    }
  };

  const deleteAccount = async () => {
    try {
      await authApi.deleteAccount();
      await firebaseAuth.signOut();
      analytics.logout();
      analytics.setUser(null);
      queryClient.clear();
      toast.success('Account deleted');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete account';
      toast.error(message);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        firebaseUser,
        dbUser,
        managedBars,
        isLoading,
        isAuthenticated,
        isAdmin,
        hasBarAccess,
        sendSignInLink,
        completeSignInWithEmailLink,
        signInWithGoogle,
        signOut,
        deleteAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
